import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getFileDetail, listFiles, type FileDetail, type FileItem } from "../../../services/files";
import {
  clearGenerationState,
  getGenerationState,
  generateWithAI,
  getTemplatePlaceholders,
  listTemplates,
  subscribeGenerationState,
  waitForGeneration,
  type DocumentTemplateItem,
  type TemplateGenerateResponse,
  uploadTemplate,
} from "../../../services/document";
import { ensurePermission } from "../../../utils/permissions";
import { getIndexStatusLabel } from "../../../utils/fileStatus";
import RIcon from "../../../components/RIcon";

type Mode = "docx" | "pptx";

const fileTypeIcon: Record<string, string> = {
  pdf: "ri-file-pdf-line",
  docx: "ri-file-word-line",
  doc: "ri-file-word-line",
  xlsx: "ri-file-excel-line",
  xls: "ri-file-excel-line",
  pptx: "ri-file-ppt-2-line",
  ppt: "ri-file-ppt-2-line",
};

function getDocTableRows(tableRows: Array<Record<string, unknown>> | undefined) {
  return (tableRows || []).filter(
    (item) => Array.isArray((item as { rows?: unknown }).rows) && ((item as { rows?: unknown[] }).rows?.length || 0) > 0
  );
}

export default function DocGenPage() {
  const { user } = useOutletContext<{ user: import("../../../services/auth").UserInfo | null }>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [mode, setMode] = useState<Mode>("docx");
  const [templates, setTemplates] = useState<DocumentTemplateItem[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [templateId, setTemplateId] = useState<string>("");
  const [topic, setTopic] = useState("");
  const [requirements, setRequirements] = useState("");
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [searchText, setSearchText] = useState("");
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [result, setResult] = useState<TemplateGenerateResponse | null>(null);
  const [generatedFileDetail, setGeneratedFileDetail] = useState<FileDetail | null>(null);
  const [error, setError] = useState("");
  const FORM_CACHE_KEY = "doc-gen:form";

  const filteredTemplates = useMemo(
    () =>
      templates.filter((item) =>
        mode === "docx"
          ? ["doc", "docx"].includes(item.file_type)
          : ["ppt", "pptx"].includes(item.file_type)
      ),
    [mode, templates]
  );

  const filteredFiles = useMemo(
    () =>
      files.filter((file) =>
        file.file_name.toLowerCase().includes(searchText.trim().toLowerCase())
      ),
    [files, searchText]
  );

  const selectedTemplate = filteredTemplates.find((item) => item.template_id === templateId) || null;
  const localTemplates = filteredTemplates.filter((item) => item.source === "local");
  const fileTemplates = filteredTemplates.filter((item) => item.source === "file");
  const previewTableRows = getDocTableRows(result?.preview?.table_rows);

  const refreshTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await listTemplates();
      setTemplates(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载模板失败");
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    refreshTemplates();
    setLoadingFiles(true);
    listFiles(0, 5000)
      .then(setFiles)
      .catch((err) => setError(err instanceof Error ? err.message : "加载文件失败"))
      .finally(() => setLoadingFiles(false));
  }, []);

  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(FORM_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as {
          mode?: Mode;
          templateId?: string;
          topic?: string;
          requirements?: string;
          selectedFileIds?: number[];
          searchText?: string;
        };
        if (parsed.mode) setMode(parsed.mode);
        if (parsed.templateId) setTemplateId(parsed.templateId);
        if (parsed.topic) setTopic(parsed.topic);
        if (parsed.requirements) setRequirements(parsed.requirements);
        if (Array.isArray(parsed.selectedFileIds)) setSelectedFileIds(parsed.selectedFileIds);
        if (parsed.searchText) setSearchText(parsed.searchText);
      }
    } catch {
      // ignore cache restore failure
    }

    const current = getGenerationState();
    if (current.status === "running") {
      setGenerating(true);
      waitForGeneration()
        .then((pendingResult) => {
          if (pendingResult) {
            setResult(pendingResult);
          }
          setGenerating(false);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : "生成失败");
          setGenerating(false);
        });
    } else if (current.status === "success" && current.result) {
      setResult(current.result);
      setGenerating(false);
    } else if (current.status === "error" && current.error) {
      setError(current.error);
      setGenerating(false);
    }

    return subscribeGenerationState((state) => {
      setGenerating(state.status === "running");
      if (state.status === "success" && state.result) {
        setResult(state.result);
      }
      if (state.status === "error" && state.error) {
        setError(state.error);
      }
    });
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      FORM_CACHE_KEY,
      JSON.stringify({ mode, templateId, topic, requirements, selectedFileIds, searchText })
    );
  }, [mode, templateId, topic, requirements, selectedFileIds, searchText]);

  useEffect(() => {
    if (!result?.file_id) return;
    let cancelled = false;
    let attempts = 0;

    const pollDetail = async () => {
      while (!cancelled && attempts < 8) {
        attempts += 1;
        try {
          const detail = await getFileDetail(result.file_id);
          if (cancelled) return;
          setGeneratedFileDetail(detail);
          if (detail.content_text || detail.index_status === "completed" || detail.index_status === "failed") {
            return;
          }
        } catch {
          // ignore and retry
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    };

    pollDetail();
    return () => {
      cancelled = true;
    };
  }, [result?.file_id]);

  useEffect(() => {
    if (!filteredTemplates.some((item) => item.template_id === templateId)) {
      setTemplateId(filteredTemplates[0]?.template_id || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, filteredTemplates.length]);

  useEffect(() => {
    if (!templateId || !templates.some((item) => item.template_id === templateId)) {
      setPlaceholders([]);
      return;
    }
    setLoadingPlaceholders(true);
    getTemplatePlaceholders(templateId)
      .then(setPlaceholders)
      .catch((err) => {
        setPlaceholders([]);
        const message = err instanceof Error ? err.message : "读取模板字段失败";
        setError(message);
        if (message.includes("模板不存在")) {
          setTemplateId("");
        }
      })
      .finally(() => setLoadingPlaceholders(false));
  }, [templateId, templates]);

  const toggleFile = (id: number) => {
    setSelectedFileIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleUploadTemplate: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadTemplate(file);
      await refreshTemplates();
      setMode(["ppt", "pptx"].includes(uploaded.file_type) ? "pptx" : "docx");
      setTemplateId(uploaded.template_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "上传模板失败");
    } finally {
      setUploading(false);
      if (event.target) event.target.value = "";
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setError("");
    setResult(null);
    setGeneratedFileDetail(null);
    try {
      const generated = await generateWithAI({
        mode,
        template_id: templateId || undefined,
        topic: topic.trim(),
        requirements: requirements.trim() || undefined,
        file_ids: selectedFileIds,
        output_filename: topic.trim().slice(0, 30) || "output",
      });
      setResult(generated);
      try {
        const detail = await getFileDetail(generated.file_id);
        setGeneratedFileDetail(detail);
      } catch {
        setGeneratedFileDetail(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "生成失败";
      setError(message);
      if (message.includes("模板不存在")) {
        setTemplateId("");
        await refreshTemplates();
      }
    } finally {
      setGenerating(false);
    }
  };

  const clearPreview = () => {
    setResult(null);
    setGeneratedFileDetail(null);
    // 也清掉用户的模板和参考文件选择，方便重新选模板生成下一份文档
    setTemplateId("");
    setSelectedFileIds([]);
    clearGenerationState();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-5 h-[calc(100vh-56px-48px)] min-h-0">
      <div className="w-full lg:w-[430px] flex-shrink-0 bg-white rounded-xl border border-gray-100 p-4 overflow-y-auto max-h-[50vh] lg:max-h-none">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[#1A2B3C]">智能文档 / PPT 生成</h2>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="h-8 px-3 rounded-lg bg-[#0EA5A0] text-white text-xs hover:bg-[#0D9590] cursor-pointer"
            disabled={uploading}
          >
            {uploading ? "上传中..." : "上传模板"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".doc,.docx,.ppt,.pptx"
            className="hidden"
            onChange={handleUploadTemplate}
          />
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {[
            { id: "docx", label: "DOCX 模式", icon: "ri-file-word-line" },
            { id: "pptx", label: "PPT 模式", icon: "ri-slideshow-line" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setMode(item.id as Mode)}
              className={`h-10 rounded-xl border text-sm font-medium cursor-pointer ${
                mode === item.id
                  ? "border-[#0EA5A0] bg-[#0EA5A0]/10 text-[#0EA5A0]"
                  : "border-gray-200 text-[#4A5568]"
              }`}
            >
              <i className={`${item.icon} mr-1`} />
              {item.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[#4A5568] mb-1">主题</label>
            <input
              className="w-full h-9 border border-gray-200 rounded-lg px-3 text-xs outline-none focus:border-[#0EA5A0]"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={mode === "pptx" ? "例如：质量体系季度汇报" : "例如：CAPA 调查报告"}
            />
          </div>
          <div>
            <label className="block text-xs text-[#4A5568] mb-1">需求说明</label>
            <textarea
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#0EA5A0] resize-y"
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              placeholder="输入生成要求、重点关注点、输出风格、时效要求等"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-[#4A5568]">选择模板</label>
            {loadingTemplates && <span className="text-[11px] text-[#718096]">加载中...</span>}
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {!!localTemplates.length && (
              <div>
                <p className="mb-2 text-[11px] font-medium text-[#718096]">模板目录</p>
                <div className="space-y-2">
                  {localTemplates.map((item) => (
                    <button
                      key={item.template_id}
                      onClick={() => setTemplateId(item.template_id)}
                      className={`w-full text-left rounded-xl border p-3 cursor-pointer ${
                        templateId === item.template_id
                          ? "border-[#0EA5A0] bg-[#0EA5A0]/10"
                          : "border-gray-200 hover:border-[#0EA5A0]/40"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-[#1A2B3C] truncate">{item.name}</p>
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] text-emerald-700">模板文件</span>
                      </div>
                      <p className="text-[11px] text-[#718096] mt-1">本地模板目录 · {item.file_type.toUpperCase()}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!!fileTemplates.length && (
              <div>
                <p className="mb-2 text-[11px] font-medium text-[#718096]">文件库中的模板文件</p>
                <div className="space-y-2">
                  {fileTemplates.map((item) => (
                    <button
                      key={item.template_id}
                      onClick={() => setTemplateId(item.template_id)}
                      className={`w-full text-left rounded-xl border p-3 cursor-pointer ${
                        templateId === item.template_id
                          ? "border-[#0EA5A0] bg-[#0EA5A0]/10"
                          : "border-gray-200 hover:border-[#0EA5A0]/40"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-[#1A2B3C] truncate">{item.name}</p>
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] text-sky-700">文件库模板</span>
                      </div>
                      <p className="text-[11px] text-[#718096] mt-1">来源文件 #{item.file_id} · {item.file_type.toUpperCase()}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!filteredTemplates.length && (
              <p className="text-xs text-[#718096]">当前模式下暂无模板，可先上传模板。</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-[#4A5568]">模板字段 / 结构提示</label>
            {loadingPlaceholders && <span className="text-[11px] text-[#718096]">解析中...</span>}
          </div>
          <div className="rounded-xl border border-gray-100 bg-[#F4F8FB] p-3 min-h-[70px]">
            {placeholders.length ? (
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map((item) => (
                  <span key={item} className="px-2 py-1 rounded-full bg-white border border-gray-200 text-xs text-[#4A5568]">
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#718096]">
                该模板没有显式占位符时，系统会自动解析段落/表格标签，并结合主题、需求和文件上下文自动填写。
              </p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-[#4A5568]">文件上下文</label>
            <span className="text-[11px] text-[#718096]">已选 {selectedFileIds.length}</span>
          </div>
          <div className="relative mb-2">
            <RIcon name="search-line" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#718096] text-xs" />
            <input
              className="w-full h-8 border border-gray-200 rounded-lg pl-7 pr-3 text-xs outline-none focus:border-[#0EA5A0]"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="搜索文件..."
            />
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {loadingFiles && <p className="text-xs text-[#718096]">正在加载文件...</p>}
            {!loadingFiles &&
              filteredFiles.map((file) => {
                const ext = (file.file_type || file.file_name.split(".").pop() || "").toLowerCase();
                return (
                  <label
                    key={file.id}
                    className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer ${
                      selectedFileIds.includes(file.id)
                        ? "border-[#0EA5A0] bg-[#0EA5A0]/10"
                        : "border-gray-200 hover:border-[#0EA5A0]/40"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFileIds.includes(file.id)}
                      onChange={() => toggleFile(file.id)}
                      className="mt-0.5 accent-[#0EA5A0]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1 text-xs text-[#1A2B3C]">
                        <i className={fileTypeIcon[ext] || "ri-file-line"} />
                        <span className="truncate">{file.file_name}</span>
                      </div>
                      <p className="text-[11px] text-[#718096] mt-1">
                        {file.index_status === "completed" ? "已索引，可作为上下文" : `索引状态：${getIndexStatusLabel(file.index_status)}`}
                      </p>
                    </div>
                  </label>
                );
              })}
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={!topic.trim() || generating}
          className="mt-4 w-full h-10 rounded-xl bg-[#0EA5A0] text-white text-sm font-semibold hover:bg-[#0D9590] disabled:opacity-40 cursor-pointer"
        >
          {generating ? "生成中..." : mode === "pptx" ? "生成 PPT" : "生成文档"}
        </button>
        {!result && (
          <p className="mt-2 text-xs text-[#718096]">
            生成完成后，文件会自动保存到你的文件库，可以到「文件管理」里继续查看。
          </p>
        )}
        {!!error && <p className="mt-2 text-xs text-red-500">{error}</p>}
      </div>

      <div className="flex-1 bg-white rounded-xl border border-gray-100 p-6 overflow-y-auto">
        <h2 className="text-sm font-semibold text-[#1A2B3C] mb-3">结果预览</h2>
        {!result ? (
          <div className="text-xs text-[#718096] space-y-2">
            <p>1. 选择 DOCX 或 PPT 模式</p>
            <p>2. 选择模板（可选）</p>
            <p>3. 输入主题与需求</p>
            <p>4. 选择文件作为上下文</p>
            <p>5. 系统会结合模板、文件上下文和用户需求自动生成结果</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-sm font-medium text-emerald-700">文档已生成成功</p>
              <p className="mt-1 text-xs text-[#4A5568]">
                生成结果已经保存到你的文件库，文件名为：
                <span className="ml-1 font-medium text-[#1A2B3C]">{result.file_name}</span>
              </p>
              <p className="mt-1 text-xs text-[#718096]">
                你可以稍后到「文件管理」里继续查看。
              </p>
            </div>
            <p className="text-sm text-[#1A2B3C] font-medium">{result.file_name}</p>
            <div className="flex items-center gap-3">
              <button
                onClick={clearPreview}
                className="h-9 px-4 rounded-lg border border-gray-200 text-xs text-[#4A5568] hover:border-gray-300 cursor-pointer"
              >
                清空结果
              </button>
              <a
                href="/workbench/files"
                className="h-9 px-4 rounded-lg border border-gray-200 text-xs text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] flex items-center"
              >
                去文件管理查看
              </a>
            </div>
            <div className="text-xs text-[#718096]">
              模式：{mode === "pptx" ? "PPT" : "DOCX"} · 模板：{selectedTemplate?.name || "无模板"}
            </div>
            {!!generatedFileDetail?.content_text && (
              <div className="rounded-xl border border-gray-100 bg-[#F4F8FB] p-4 text-xs text-[#4A5568] space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-[#1A2B3C]">文档内容预览</p>
                  <span className="text-[11px] text-[#718096]">索引状态：{getIndexStatusLabel(generatedFileDetail.index_status)}</span>
                </div>
                <div className="rounded-lg bg-white border border-gray-200 p-3 max-h-[360px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap break-words leading-6 text-[#4A5568] font-sans">
                    {generatedFileDetail.content_text}
                  </pre>
                </div>
              </div>
            )}
            {!generatedFileDetail?.content_text && generatedFileDetail && (
              <div className="rounded-xl border border-gray-100 bg-[#F4F8FB] p-4 text-xs text-[#4A5568] space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-[#1A2B3C]">文件预览状态</p>
                  <span className="text-[11px] text-[#718096]">索引状态：{getIndexStatusLabel(generatedFileDetail.index_status)}</span>
                </div>
                <p className="text-[#718096]">
                  生成文件已保存，正在同步更多可预览内容。若当前还未出现正文预览，可稍后在文件管理中查看。
                </p>
              </div>
            )}
            {result.preview?.mode === "pptx" && result.preview.slides && (
              <div className="rounded-xl border border-gray-100 bg-[#F4F8FB] p-4 text-xs text-[#4A5568] space-y-2">
                <p className="font-medium text-[#1A2B3C]">预览结构</p>
                {result.preview.slides.map((slide, index) => (
                  <div key={index} className="rounded-lg bg-white border border-gray-200 p-3">
                    <p className="font-medium text-[#1A2B3C]">第 {index + 1} 页 · {String((slide as any).title || (slide as any).heading || "未命名")}</p>
                    {(slide as any).subtitle && <p className="mt-1 text-[#718096]">{String((slide as any).subtitle)}</p>}
                    {Array.isArray((slide as any).bullet_points) && <ul className="mt-2 list-disc pl-5 space-y-1">{((slide as any).bullet_points as string[]).map((item) => <li key={item}>{item}</li>)}</ul>}
                  </div>
                ))}
              </div>
            )}
            {result.preview?.mode === "docx" && (
              <div className="rounded-xl border border-gray-100 bg-[#F4F8FB] p-4 text-xs text-[#4A5568] space-y-2">
                <p className="font-medium text-[#1A2B3C]">预览结构</p>
                {(result.preview.fields && Object.keys(result.preview.fields).length > 0) || previewTableRows.length > 0 ? (
                  <div className="space-y-3">
                    {result.preview.fields && Object.keys(result.preview.fields).length > 0 && (
                      <div className="space-y-2">
                        {Object.entries(result.preview.fields).map(([key, value]) => (
                          <div key={key} className="rounded-lg bg-white border border-gray-200 p-3">
                            <p className="font-medium text-[#1A2B3C]">{key}</p>
                            <p className="mt-1 whitespace-pre-wrap text-[#718096]">{value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {previewTableRows.map((item, index) => {
                      const rows = Array.isArray(item.rows) ? item.rows : [];
                      return (
                        <div key={`${item.table_index ?? index}`} className="rounded-lg bg-white border border-gray-200 p-3">
                          <p className="font-medium text-[#1A2B3C]">表格 {typeof item.table_index === "number" ? item.table_index + 1 : index + 1}</p>
                          <div className="mt-2 space-y-1.5">
                            {rows.map((row, rowIndex) => (
                              <div key={rowIndex} className="rounded-md bg-[#F8FAFC] px-2.5 py-2 text-[#718096]">
                                {Array.isArray(row) ? row.map((cell) => String(cell)).join(" | ") : String(row)}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[#718096]">当前模板无可直接预览字段，系统已按模板结构生成结果。</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
